/**
 * @class ButtonsCard
 * @description Web component que gestiona las acciones de productos en las cards (añadir al carrito, 
 * aumentar/disminuir cantidad) y emite eventos para sincronizar cambios con el carrito de Shopify.
 * Este componente delega eventos a todos los botones con atributo data-action.
*/
class ButtonsCard extends HTMLElement {
  constructor() {
      super();
  }

  /**
   * @method connectedCallback
   * @description Inicializa propiedades y configura el listener de eventos cuando el componente
   * se añade al DOM. Usa delegación de eventos para manejar todos los botones con data-action.
   */
  connectedCallback() {
    this.id = this.getAttribute('id');                       // ID único del componente
    this.sectionId = this.getAttribute('data-section-id');   // ID de la sección para re-renderizado
    this.productTitle = this.getAttribute('data-product-title'); // Título del producto
    this.variantId = this.getAttribute('data-variant-id');   // ID de la variante del producto
    this.actionElement = this.querySelectorAll('[data-action]'); // Elementos con acciones
    this.dataStock = this.getAttribute('data-stock');        // Stock disponible del producto
    this.quantityElement = this.querySelector('[data-quantity-event]'); // Elemento que muestra la cantidad

    // Handler único delegado para TODOS los botones con data-action
    // Usa event.target.closest para encontrar el botón clickeado o su ancestro con data-action
    this.onClick = (event) => {
      event.preventDefault();
      const btn = event.target.closest('[data-action]');
      if (!btn) return;
      this.handleEvent(btn);
    };

    this.addEventListener('click', this.onClick);
  }

  /**
   * @method disconnectedCallback
   * @description Limpia los event listeners cuando el componente se elimina del DOM
   * para evitar memory leaks.
   */
  disconnectedCallback() {
    // Eliminamos el handler delegado cuando el componente se desmonta
    this.removeEventListener('click', this.onClick);
  }

  /**
   * @method handleEvent
   * @description Determina qué acción ejecutar según el atributo `data-action` del elemento clicado.
   * Maneja tres tipos de acciones: añadir al carrito, aumentar cantidad y disminuir cantidad.
   * @param {HTMLElement} element - Elemento que disparó el evento (botón con data-action).
  */
  handleEvent(element) {
      const action = element.getAttribute('data-action')
      if (action === 'add-to-cart') {
          this.addToCart()
      }
      if (action === 'plus-quantity') {
          /* this.querySelector('[data-quantity-event]').classList.add('is-updating'); */
          this.handleUpdateCart('plus')
      }
      if (action === 'minus-quantity') {
          /* this.querySelector('[data-quantity-event]').classList.add('is-updating'); */
          this.handleUpdateCart('minus')
      }
  }

  /**
   * @method handleUpdateCart
   * @description Actualiza la cantidad de un producto en el carrito (incrementa o reduce)
   * y emite un evento personalizado para que card-product maneje la actualización.
   * Incluye validación de stock y debounce para evitar múltiples peticiones.
   * @param {'plus'|'minus'} action - Acción que determina si se aumenta o reduce la cantidad.
  */
  handleUpdateCart(action) {
    // Calculamos la nueva cantidad según la acción
    const currentQuantity = parseInt(this.quantityElement.textContent);
    const newQuantity = action === 'plus' ? currentQuantity + 1 : currentQuantity - 1;

    // Validamos que la cantidad esté dentro de los límites permitidos
    if(
      newQuantity < 0 || 
      newQuantity > parseInt(this.dataStock)
    ){
      return;
    }

    // Actualizamos la UI inmediatamente para feedback visual
    this.quantityElement.textContent = newQuantity;

    // Preparamos los datos para el evento de actualización
    const detail = {
      type: 'update-quantity',
      variant_id: this.variantId,
      product_id: null,
      product_title: this.productTitle,
      quantity: newQuantity,
      line_index: null
    };

    // Debounce: cancelamos timeout previo si existe
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }

    // Programamos la actualización con delay para evitar múltiples peticiones
    this.updateTimeout = setTimeout(() => {
      this.lockButtons();

      // Emitimos evento para que card-product maneje la actualización del carrito
      this.dispatchEvent(new CustomEvent('pb:card:update', {
        detail,
        bubbles: true,
      }));
    }, 500);
  }

  /**
   * @method addToCart
   * @description Emite un evento personalizado para añadir el producto al carrito con cantidad 1.
   * El evento es capturado por card-product que se encarga de la comunicación con Shopify.
   */
  addToCart(){
    const detail = {
      type: 'add-to-cart',
      variant_id: this.variantId,
      product_id: null,
      product_title: this.productTitle,
      quantity: 1,
      line_index: null,
      action: null
    };

    // Emitimos evento para que card-product maneje la adición al carrito
    this.dispatchEvent(new CustomEvent('pb:card:update', {
      detail,
      bubbles: true,
    }));
  }

  /**
   * @method lockButtons
   * @description Deshabilita todos los botones del componente para evitar múltiples clicks
   * mientras se procesa la actualización del carrito.
   * No incluye método de desbloqueo ya que card-product se encarga de re-renderizar
   * el componente completo después de la actualización, lo que restablece los botones
   * automáticamente al estado normal.
   */
  lockButtons() {
    this.querySelectorAll('button').forEach(button => {
      button.setAttribute('disabled', 'disabled');
      button.classList.add('disabled');
    });
  }
}

customElements.define('buttons-card', ButtonsCard);

class PdpButton extends ButtonsCard {
  constructor() {
      super();
  }
}

customElements.define('pdp-button', PdpButton);